import type { Alert } from '@rsos/reduxDataStore/src/features/alerts/alerts.types';
import type {
  AdrDataByTopic,
  AlertResult,
  AlertsById,
  SearchResultItem,
} from '../../GlobalMapSearch.types';

interface RunAlertsSearchArgs {
  text: string;
  alerts: AlertsById | undefined;
  removedAlerts: string[] | undefined;
  /**
   * Snapshot of `state.adrData.data` keyed by topic (`alert://<alertId>`).
   * Used to surface matches against ADR-only fields like train car info
   * (equipment_id, railcarSequence) that aren't on the base alert object.
   */
  adrData: AdrDataByTopic | undefined;
  collected: SearchResultItem[];
  flush: () => void;
}

/** Hard cap on how deep we'll recurse into a single ADR payload. */
const MAX_ADR_DEPTH = 8;
/** Hard cap on how many string leaves we'll inspect per alert to bound work. */
const MAX_ADR_LEAVES_PER_ALERT = 250;

interface MatchableField {
  /** Human-readable field label surfaced in the dropdown sublabel. */
  label: string;
  /** Raw leaf value to match against (already trimmed-or-empty). */
  value: string | null | undefined;
}

/**
 * Build the list of searchable fields for one alert. Order matters because
 * the matcher uses first-match-wins semantics — earlier entries take
 * precedence when surfacing the matched field/value back to the UI.
 */
function buildMatchableFields(alert: Alert): MatchableField[] {
  const fields: MatchableField[] = [
    { label: 'Alert ID', value: alert.alert_id },
    {
      label: 'Emergency Type',
      value: alert.emergency_type?.display_name,
    },
    { label: 'Emergency Type', value: alert.emergency_type?.name },
    { label: 'Status', value: alert.status?.display_name },
    { label: 'Status', value: alert.status?.name },
    { label: 'Description', value: alert.description },
    {
      label: 'Authorized Entity',
      value: alert.authorized_entity?.name,
    },
    { label: 'Service Provider', value: alert.service_provider_name },
    { label: 'Site Type', value: alert.site_type?.display_name },
  ];

  // The most recent location may live on `location` or in the tail of
  // `location_history`; surface the civic components from whichever is
  // populated so address-style queries hit even when only one path exists.
  const civicSources: Alert['location'][] = [];
  if (alert.location && 'civic' in alert.location) {
    civicSources.push(alert.location);
  }
  const history = alert.location_history;
  if (Array.isArray(history)) {
    for (let i = history.length - 1; i >= 0; i--) {
      const entry = history[i];
      if (entry && 'civic' in entry) {
        civicSources.push(entry);
        break;
      }
    }
  }
  for (const source of civicSources) {
    if (!source || !('civic' in source)) continue;
    const civic = source.civic;
    fields.push(
      { label: 'Location · Street', value: civic?.street_1 },
      { label: 'Location · Street', value: civic?.street_2 },
      { label: 'Location · City', value: civic?.city },
      { label: 'Location · State', value: civic?.state },
      { label: 'Location · Zip', value: civic?.zip_code },
      { label: 'Location · Name', value: civic?.name },
    );
  }

  return fields;
}

/**
 * Visit every string-ish leaf inside `value`, tagging each with the
 * dot-joined object path that produced it. Numeric array indices are
 * dropped from the displayed path so users see e.g. `cars.equipment_id`
 * instead of `cars.0.equipment_id`. The visitor returns `true` to stop
 * the walk early (used to honor the leaf cap and the first-match exit).
 */
function walkStringLeaves(
  value: unknown,
  path: string[],
  depth: number,
  visit: (leaf: { path: string; value: string }) => boolean,
): boolean {
  if (value == null || depth > MAX_ADR_DEPTH) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return visit({
      path: path.filter(p => !/^\d+$/.test(p)).join('.'),
      value: trimmed,
    });
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return visit({
      path: path.filter(p => !/^\d+$/.test(p)).join('.'),
      value: String(value),
    });
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (walkStringLeaves(value[i], [...path, String(i)], depth + 1, visit)) {
        return true;
      }
    }
    return false;
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value as Record<string, unknown>)) {
      if (
        walkStringLeaves(
          (value as Record<string, unknown>)[k],
          [...path, k],
          depth + 1,
          visit,
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Recursively find every object that looks like a train car entry
 * (`equipment_id` + `railcarSequence`) and synthesize the human-readable
 * label the alert pane shows for it: `Car <seq> - <equipment_id>`.
 *
 * Surfacing this synthesized form lets the user search "car 110" or
 * "car 110 - cttx 693151" verbatim — the underlying fields live in
 * separate leaves and would otherwise miss multi-token queries that
 * span the synthesized label.
 */
function collectCarLabels(value: unknown, depth: number, out: string[]): void {
  if (value == null || depth > MAX_ADR_DEPTH) return;
  if (Array.isArray(value)) {
    for (const v of value) collectCarLabels(v, depth + 1, out);
    return;
  }
  if (typeof value !== 'object') return;
  const obj = value as Record<string, unknown>;
  const equipmentId = obj.equipment_id;
  const railcarSequence = obj.railcarSequence;
  if (
    (typeof equipmentId === 'string' && equipmentId.trim()) ||
    (typeof railcarSequence === 'string' && railcarSequence.trim()) ||
    typeof railcarSequence === 'number'
  ) {
    const seq =
      typeof railcarSequence === 'number'
        ? String(railcarSequence)
        : (railcarSequence as string | undefined)?.trim();
    const eq = typeof equipmentId === 'string' ? equipmentId.trim() : undefined;
    if (seq && eq) out.push(`Car ${seq} - ${eq}`);
    else if (seq) out.push(`Car ${seq}`);
    else if (eq) out.push(`Car ${eq}`);
  }
  for (const k of Object.keys(obj)) {
    collectCarLabels(obj[k], depth + 1, out);
  }
}

interface AdrMatch {
  fieldPath: string;
  value: string;
}

/**
 * Search every string leaf under one alert's parsed ADR records for
 * `lowerQuery`. Returns the first hit (or `null` if nothing matched), with
 * a human-readable field path prefixed by the entry's display title (or
 * orgName) so the dropdown can show *why* the alert matched.
 */
function searchAlertAdr(
  parsedEntries: Record<string, unknown>[] | undefined,
  lowerQuery: string,
): AdrMatch | null {
  if (!parsedEntries || parsedEntries.length === 0) return null;

  let visited = 0;
  for (const entry of parsedEntries) {
    if (!entry || typeof entry !== 'object') continue;
    const schema = entry.display_schema as { title?: unknown } | undefined;
    const orgTitle =
      (typeof schema?.title === 'string' && schema.title.trim()) ||
      (typeof entry.orgName === 'string' && entry.orgName.trim()) ||
      'Alert Data';

    // Walk the parsed payload. Per the adrDataSlice parser the original
    // payload usually lives under `body`, but some entries spread the
    // payload onto the entry itself — walk the whole entry minus a few
    // metadata keys to cover both cases.
    const payload: Record<string, unknown> = {};
    for (const k of Object.keys(entry)) {
      if (k === 'display_schema' || k === 'orgName' || k === 'suffix') continue;
      payload[k] = entry[k];
    }

    // Synthesized "Car X - EQUIP_ID" labels first so multi-token queries
    // like "car 110 - cttx 693151" hit before falling into per-leaf hits.
    const carLabels: string[] = [];
    collectCarLabels(payload, 0, carLabels);
    for (const label of carLabels) {
      visited++;
      if (label.toLowerCase().includes(lowerQuery)) {
        return { fieldPath: `${orgTitle} · Car`, value: label };
      }
      if (visited >= MAX_ADR_LEAVES_PER_ALERT) return null;
    }

    let match: AdrMatch | null = null;
    walkStringLeaves(payload, [], 0, leaf => {
      visited++;
      if (leaf.value.toLowerCase().includes(lowerQuery)) {
        match = {
          fieldPath: leaf.path ? `${orgTitle} · ${leaf.path}` : orgTitle,
          value: leaf.value,
        };
        return true;
      }
      return visited >= MAX_ADR_LEAVES_PER_ALERT;
    });
    if (match) return match;
    if (visited >= MAX_ADR_LEAVES_PER_ALERT) return null;
  }
  return null;
}

/**
 * Synchronous filter over the alert queue (`state.incidents.alerts`).
 *
 * Match precedence per alert:
 *   1. First matching base-alert field (id, type, status, location, etc.)
 *      from `buildMatchableFields`.
 *   2. If no base hit, walk the alert's ADR (`state.adrData.data`) so
 *      train-car fields like `equipment_id` / `railcarSequence` and the
 *      synthesized `Car <seq> - <equipment_id>` label are searchable.
 *
 * Empty/removed alerts and queries shorter than 3 chars short-circuit, so
 * single-letter typing doesn't flood the dropdown with every alert in the
 * tenant.
 */
export function runAlertsSearch({
  text,
  alerts,
  removedAlerts,
  adrData,
  collected,
  flush,
}: RunAlertsSearchArgs): void {
  if (!alerts) return;

  const lowerQuery = text.trim().toLowerCase();
  if (lowerQuery.length < 3) return;

  const removed = new Set(removedAlerts ?? []);

  let appended = false;
  for (const alertId of Object.keys(alerts)) {
    if (removed.has(alertId)) continue;
    const alert = alerts[alertId];
    if (!alert) continue;

    let matchedField: string | undefined;
    let matchedText: string | undefined;

    for (const field of buildMatchableFields(alert)) {
      const value = field.value;
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (trimmed.toLowerCase().includes(lowerQuery)) {
        matchedField = field.label;
        matchedText = trimmed;
        break;
      }
    }

    // Fall through to ADR-walk so train car ids and other non-base fields
    // surface their owning alert in results.
    if (!matchedField && adrData) {
      const topic = `alert://${alertId}`;
      const adrEntry = adrData[topic];
      const adrMatch = searchAlertAdr(adrEntry?.data, lowerQuery);
      if (adrMatch) {
        matchedField = adrMatch.fieldPath;
        matchedText = adrMatch.value;
      }
    }

    if (!matchedField) continue;

    const result: AlertResult = {
      alertId,
      alert,
      matchedField,
      matchedText,
    };
    collected.push({ type: 'alert', data: result });
    appended = true;
  }

  if (appended) flush();
}
